import type { Rect } from "../../common/geometry.js";
import { isOhlc, type Ohlc } from "./chartScale.js";

/**
 * The arithmetic behind the financial chart types: price transforms that turn a series of periods
 * into the shapes Heikin-Ashi, Renko, Kagi and point-and-figure charts draw, and the layouts of a
 * waterfall, a market depth chart, a volume profile and a treemap. All pure, so each can be checked
 * without a GPU and used without a `Chart`.
 */

type Values = readonly (number | null | undefined)[];

const usable = (v: number | null | undefined): v is number => v !== null && v !== undefined && Number.isFinite(v);

/**
 * Heikin-Ashi candles: each close the average of the period's four prices, each open the midpoint
 * of the previous candle's body, highs and lows stretched to take them in. The result smooths out
 * the noise so a run reads as a run. A gap restarts the smoothing.
 */
export function heikinAshi(prices: readonly (Ohlc | null)[]): (Ohlc | null)[] {
  let prev: Ohlc | null = null;
  return prices.map((p) => {
    if (!isOhlc(p)) {
      prev = null;
      return null;
    }
    const close = (p.open + p.high + p.low + p.close) / 4;
    const open = prev ? (prev.open + prev.close) / 2 : (p.open + p.close) / 2;
    const next = { open, close, high: Math.max(p.high, open, close), low: Math.min(p.low, open, close) };
    prev = next;
    return next;
  });
}

/**
 * Renko bricks: a new brick each time the price moves a whole `boxSize` beyond the last brick, up or
 * down - and a turn needs two boxes, since it starts from the far side of the brick before. Time
 * drops out: the bricks are spaced evenly, one per box moved, however long each took.
 */
export function renko(values: Values, boxSize: number): Ohlc[] {
  const box = Math.abs(boxSize);
  const out: Ohlc[] = [];
  if (!(box > 0)) return out;
  let base: number | null = null;
  let top = 0;
  let bottom = 0;
  for (const v of values) {
    if (!usable(v)) continue;
    if (base === null) {
      base = Math.floor(v / box) * box;
      top = bottom = base;
      continue;
    }
    while (v >= top + box) {
      out.push({ open: top, close: top + box, high: top + box, low: top });
      bottom = top;
      top += box;
    }
    while (v <= bottom - box) {
      out.push({ open: bottom, close: bottom - box, high: bottom, low: bottom - box });
      top = bottom;
      bottom -= box;
    }
  }
  return out;
}

/**
 * Kagi turning points: the line runs on in its direction for as long as the price does, and turns
 * only when it comes back by `reversal` from the extreme. The points are where it turned, and where
 * it stands now; a `kagi` series draws them, thick where the line has climbed past its last
 * shoulder and thin where it has fallen through its last waist.
 */
export function kagi(values: Values, reversal: number): number[] {
  const rev = Math.abs(reversal);
  const out: number[] = [];
  let direction = 0;
  for (const v of values) {
    if (!usable(v)) continue;
    if (out.length === 0) {
      out.push(v);
      continue;
    }
    const last = out[out.length - 1]!;
    if (direction === 0) {
      if (Math.abs(v - last) >= rev) {
        direction = Math.sign(v - last);
        out.push(v);
      }
      continue;
    }
    if ((direction > 0 && v > last) || (direction < 0 && v < last)) {
      out[out.length - 1] = v;
    } else if (Math.abs(v - last) >= rev) {
      direction = -direction;
      out.push(v);
    }
  }
  return out;
}

/**
 * Point-and-figure columns: a column of Xs while the price rises a box at a time, of Os while it
 * falls, and a new column only when it turns back `reversal` boxes. Each column comes back as a
 * period - `open` where it started, `close` where it ended - for a `pointfigure` series to draw.
 */
export function pointFigure(values: Values, boxSize: number, reversal = 3): Ohlc[] {
  const box = Math.abs(boxSize);
  const out: Ohlc[] = [];
  if (!(box > 0)) return out;
  let start: number | null = null;
  let end = 0;
  let rising: boolean | null = null;
  const flush = () => {
    if (start === null || rising === null) return;
    out.push({ open: start, close: end, high: Math.max(start, end), low: Math.min(start, end) });
  };
  for (const v of values) {
    if (!usable(v)) continue;
    const level = Math.floor(v / box) * box;
    if (start === null) {
      start = end = level;
      continue;
    }
    if (rising === null) {
      if (Math.abs(level - start) >= box) {
        rising = level > start;
        end = level;
      }
      continue;
    }
    if (rising ? level > end : level < end) {
      end = level;
    } else if (Math.abs(level - end) >= reversal * box) {
      flush();
      start = rising ? end - box : end + box;
      end = level;
      rising = !rising;
    }
  }
  flush();
  return out;
}

/** One bar of a waterfall: where it floats from and to, and whether it is a running total. */
export interface WaterfallStep {
  from: number;
  to: number;
  total: boolean;
}

/**
 * A waterfall's bars: each change floats from the running sum to the running sum plus it; each
 * index in `totals` is a bar from zero to the sum so far, its own value ignored. A gap leaves the
 * sum where it was and draws nothing.
 */
export function waterfall(deltas: Values, totals: ReadonlySet<number> = new Set()): (WaterfallStep | null)[] {
  let sum = 0;
  return deltas.map((d, i) => {
    if (totals.has(i)) return { from: 0, to: sum, total: true };
    if (!usable(d)) return null;
    const from = sum;
    sum += d;
    return { from, to: sum, total: false };
  });
}

export interface DepthLevel {
  price: number;
  size: number;
}

export interface DepthSide {
  /** Prices, rising. */
  x: number[];
  /** The size available at that price or better. */
  values: number[];
}

/**
 * The two sides of an order book as a market depth chart draws them: at each price, how much could
 * be traded there or better - bids summed downward from the best (highest) bid, asks upward from
 * the best (lowest) ask - each side given in rising price, ready for a stepped area on a linear x.
 */
export function depth(bids: readonly DepthLevel[], asks: readonly DepthLevel[]): { bids: DepthSide; asks: DepthSide } {
  const side = (levels: readonly DepthLevel[], best: "high" | "low"): DepthSide => {
    const sorted = levels
      .filter((l) => usable(l.price) && usable(l.size) && l.size > 0)
      .sort((a, b) => (best === "high" ? b.price - a.price : a.price - b.price));
    let total = 0;
    const out = sorted.map((l) => ({ price: l.price, total: (total += l.size) }));
    if (best === "high") out.reverse();
    return { x: out.map((l) => l.price), values: out.map((l) => l.total) };
  };
  return { bids: side(bids, "high"), asks: side(asks, "low") };
}

export interface VolumeProfile {
  /** The bottom of the lowest bin and the top of the highest. */
  low: number;
  high: number;
  /** How tall each bin is. */
  step: number;
  /** The volume traded in each bin, lowest first. */
  volumes: number[];
}

/**
 * Volume by price: each period's volume spread evenly over the price range it traded across, then
 * summed into `bins` equal slices between the lowest low and the highest high. A period with no
 * range puts all of its volume in the bin holding its close.
 */
export function volumeProfile(prices: readonly (Ohlc | null)[], volume: Values, bins = 24): VolumeProfile | null {
  const n = Math.max(1, Math.floor(bins));
  let low = Infinity;
  let high = -Infinity;
  prices.forEach((p, i) => {
    if (!isOhlc(p) || !usable(volume[i])) return;
    low = Math.min(low, p.low);
    high = Math.max(high, p.high);
  });
  if (!(high >= low)) return null;
  if (high === low) high = low + 1;
  const step = (high - low) / n;
  const volumes = new Array<number>(n).fill(0);
  const binOf = (price: number) => Math.min(n - 1, Math.max(0, Math.floor((price - low) / step)));
  prices.forEach((p, i) => {
    const v = volume[i];
    if (!isOhlc(p) || !usable(v) || v <= 0) return;
    const range = p.high - p.low;
    if (!(range > 0)) {
      volumes[binOf(p.close)]! += v;
      return;
    }
    const first = binOf(p.low);
    const last = binOf(p.high);
    for (let b = first; b <= last; b++) {
      const lo = Math.max(p.low, low + b * step);
      const hi = Math.min(p.high, low + (b + 1) * step);
      if (hi > lo) volumes[b]! += (v * (hi - lo)) / range;
    }
  });
  return { low, high, step, volumes };
}

/**
 * A squarified treemap: `rect` cut into one rectangle per value, each with an area in proportion
 * to it, laid in rows that keep the rectangles as near square as they can be (Bruls, Huizing and
 * van Wijk). Biggest first; a value of zero, below, or missing gets no rectangle but keeps its
 * index. Gaps between tiles are the caller's to leave.
 */
export function squarify(values: Values, rect: Rect): (Rect | null)[] {
  const out: (Rect | null)[] = values.map(() => null);
  const items = values
    .map((v, index) => ({ index, value: usable(v) && v > 0 ? v : 0 }))
    .filter((it) => it.value > 0)
    .sort((a, b) => b.value - a.value);
  const total = items.reduce((s, it) => s + it.value, 0);
  if (!(total > 0) || rect.width <= 0 || rect.height <= 0) return out;
  const scale = (rect.width * rect.height) / total;
  const areas = items.map((it) => ({ index: it.index, area: it.value * scale }));

  let free = { ...rect };
  let row: { index: number; area: number }[] = [];
  const worst = (r: { area: number }[], side: number) => {
    const sum = r.reduce((s, it) => s + it.area, 0);
    let max = 0;
    let min = Infinity;
    for (const it of r) {
      max = Math.max(max, it.area);
      min = Math.min(min, it.area);
    }
    return Math.max((side * side * max) / (sum * sum), (sum * sum) / (side * side * min));
  };
  const layRow = (r: { index: number; area: number }[]) => {
    const sum = r.reduce((s, it) => s + it.area, 0);
    if (free.width >= free.height) {
      // A column down the left of what is free.
      const w = sum / free.height;
      let y = free.y;
      for (const it of r) {
        const h = it.area / w;
        out[it.index] = { x: free.x, y, width: w, height: h };
        y += h;
      }
      free = { x: free.x + w, y: free.y, width: Math.max(0, free.width - w), height: free.height };
    } else {
      // A row along the top.
      const h = sum / free.width;
      let x = free.x;
      for (const it of r) {
        const w = it.area / h;
        out[it.index] = { x, y: free.y, width: w, height: h };
        x += w;
      }
      free = { x: free.x, y: free.y + h, width: free.width, height: Math.max(0, free.height - h) };
    }
  };
  for (const it of areas) {
    const side = Math.min(free.width, free.height);
    if (row.length === 0 || worst([...row, it], side) <= worst(row, side)) {
      row.push(it);
      continue;
    }
    layRow(row);
    row = [it];
  }
  if (row.length) layRow(row);
  return out;
}
